
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useEffect, useState } from "react";

const employeeRoles = ['Admin', '2D', '3D', 'UI', 'Design', 'Audio', 'Tech Art', 'QA', 'Dev'] as const;

const formSchema = z.object({
  name: z.string().min(2, {
    message: "El nombre debe tener al menos 2 caracteres",
  }),
  email: z.string().email({
    message: "Dirección de correo inválida",
  }),
  role: z.enum(employeeRoles, {
    required_error: "Selecciona un rol",
  }),
});

export function EmployeeForm() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState<number | null>(null);

  useEffect(() => {
    const fetchCompanyId = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("No user found");

        const { data: userInfo } = await supabase
          .from("users")
          .select("company_id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!userInfo?.company_id) throw new Error("No company associated with user");
        
        setCompanyId(userInfo.company_id);
      } catch (error) {
        console.error('Error fetching company ID:', error);
        toast({
          title: "Error",
          description: "Failed to fetch company information",
          variant: "destructive",
        });
      }
    };

    fetchCompanyId();
  }, [toast]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "Admin",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      if (!companyId) {
        throw new Error("No company ID available");
      }

      console.log('Attempting to add employee:', { ...values, company_id: companyId });
      
      // Add default values required by the employees table schema
      const { data, error } = await supabase
        .from('employees')
        .insert({
          name: values.name,
          email: values.email, 
          role: values.role,
          company_id: companyId,
          // Add required default values
          base_salary: 0,
          base_salary_currency: 'COP',
          bonuses: 0,
          transport: 0,
          contract_type: 'Permanent'
        })
        .select()
        .single();
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Employee added successfully:', data);
      
      toast({
        title: "Success",
        description: "Employee added successfully",
      });

      queryClient.invalidateQueries({ queryKey: ["employees"] });
      form.reset();
    } catch (error) {
      console.error('Error adding employee:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add employee",
        variant: "destructive",
      });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="john@example.com" type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {employeeRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={!companyId}>Submit</Button>
      </form>
    </Form>
  );
}
